import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import TaskRow from "@/components/TaskRow";
import TaskEditDialog from "@/components/TaskEditDialog";
import TaskDialog from "@/components/TaskDialog";
import { Plus, CheckCircle2, Circle, Clock, Flame, ArrowDownLeft, ArrowUpRight, ListTodo, AlertTriangle, PauseCircle, ShieldAlert, Users2, Moon } from "lucide-react";
import { SleepingIndicator, FreshlyActivatedIndicator } from "@/components/SleepingIndicators";
import { isTaskSleeping, isFreshlyActivated, activationSentence, useTaskById, useCollabById } from "@/lib/taskLinks";

const FILTERS = [
  { key: "open", label: "Otvoreni", icon: Circle },
  { key: "done", label: "Završeni", icon: CheckCircle2 },
  { key: "overdue", label: "Prekoračeni", icon: AlertTriangle },
  { key: "today", label: "Danas", icon: Clock },
  { key: "high", label: "Visok prioritet", icon: Flame },
  { key: "to_me", label: "Duguju meni", icon: ArrowDownLeft },
  { key: "to_them", label: "Ja dugujem", icon: ArrowUpRight },
  { key: "waiting", label: "Čekam", icon: PauseCircle },
  { key: "blocked", label: "Blokirani", icon: ShieldAlert },
  { key: "cross", label: "Praćeni", icon: Users2 },
  { key: "uskoro", label: "Uskoro", icon: Moon },
  { key: "all", label: "Svi", icon: ListTodo },
];

export default function Tasks() {
  const [sp, setSp] = useSearchParams();
  const filter = sp.get("filter") || "open";
  const [tasks, setTasks] = useState([]);
  const [collabs, setCollabs] = useState([]);
  const [projects, setProjects] = useState([]);
  const [editing, setEditing] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);

  const load = async () => {
    const [t, c, p] = await Promise.all([
      api.get("/tasks"),
      api.get("/collaborators"),
      api.get("/projects"),
    ]);
    setTasks(t.data); setCollabs(c.data); setProjects(p.data);
  };
  useEffect(() => { load(); }, []);

  const collabById = useCollabById(collabs);
  const taskById = useTaskById(tasks);
  const today = new Date().toISOString().slice(0, 10);

  const visible = useMemo(() => {
    switch (filter) {
      case "open": return tasks.filter((t) => t.status === "open");
      case "done": return tasks.filter((t) => t.status === "done");
      case "overdue": return tasks.filter((t) => t.status !== "done" && t.due_date && t.due_date < today && !isTaskSleeping(t, taskById));
      case "today": return tasks.filter((t) => t.status !== "done" && t.due_date === today && !isTaskSleeping(t, taskById));
      case "high": return tasks.filter((t) => t.status !== "done" && t.priority === "visok" && !isTaskSleeping(t, taskById));
      case "to_me": return tasks.filter((t) => t.status !== "done" && t.direction === "to_me" && !t.from_collaborator_id);
      case "to_them": return tasks.filter((t) => t.status !== "done" && t.direction === "to_them" && !t.from_collaborator_id);
      case "waiting": return tasks.filter((t) => t.status === "waiting");
      case "blocked": return tasks.filter((t) => t.status === "blocked");
      case "cross": return tasks.filter((t) => t.status !== "done" && !!t.from_collaborator_id);
      case "uskoro": return tasks.filter((t) => isTaskSleeping(t, taskById));
      case "all": default: return tasks;
    }
  }, [tasks, filter, today, taskById]);

  const counts = useMemo(() => ({
    open: tasks.filter((t) => t.status === "open").length,
    done: tasks.filter((t) => t.status === "done").length,
    overdue: tasks.filter((t) => t.status !== "done" && t.due_date && t.due_date < today && !isTaskSleeping(t, taskById)).length,
    today: tasks.filter((t) => t.status !== "done" && t.due_date === today && !isTaskSleeping(t, taskById)).length,
    high: tasks.filter((t) => t.status !== "done" && t.priority === "visok" && !isTaskSleeping(t, taskById)).length,
    to_me: tasks.filter((t) => t.status !== "done" && t.direction === "to_me" && !t.from_collaborator_id).length,
    to_them: tasks.filter((t) => t.status !== "done" && t.direction === "to_them" && !t.from_collaborator_id).length,
    waiting: tasks.filter((t) => t.status === "waiting").length,
    blocked: tasks.filter((t) => t.status === "blocked").length,
    cross: tasks.filter((t) => t.status !== "done" && !!t.from_collaborator_id).length,
    uskoro: tasks.filter((t) => isTaskSleeping(t, taskById)).length,
    all: tasks.length,
  }), [tasks, today, taskById]);

  const toggle = async (t) => {
    await api.patch(`/tasks/${t.id}`, { status: t.status === "done" ? "open" : "done" });
    load();
  };

  const active = FILTERS.find((f) => f.key === filter) || FILTERS[0];

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-4">
        <div>
          <div className="overline">Zadaci</div>
          <h1 className="mt-1 text-3xl md:text-4xl font-heading font-semibold tracking-tight">
            {active.label}
          </h1>
          <p className="text-ink-muted mt-2">Klikni na task da uređuješ, menjaš prioritet ili dodaš napomenu.</p>
        </div>
        <button className="btn-primary" onClick={() => setCreateOpen(true)} data-testid="tasks-new-btn">
          <Plus className="w-4 h-4" /> Novi task
        </button>
      </div>

      <div className="flex flex-wrap gap-2">
        {FILTERS.map((f) => {
          const on = filter === f.key;
          return (
            <button
              key={f.key}
              data-testid={`filter-${f.key}`}
              onClick={() => setSp({ filter: f.key })}
              className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md border text-sm transition-colors ${
                on ? "bg-forest text-white border-forest" : "bg-white text-ink border-line hover:bg-surface-secondary"
              }`}
            >
              <f.icon className="w-4 h-4" /> {f.label}
              <span className={`text-xs ${on ? "opacity-80" : "text-ink-muted"}`}>({counts[f.key] ?? 0})</span>
            </button>
          );
        })}
      </div>

      <div className="surface-card">
        {visible.length === 0 ? (
          <div className="p-10 text-center text-ink-muted">Nema taskova u ovom pregledu.</div>
        ) : (
          visible.map((t) => {
            const sleeping = isTaskSleeping(t, taskById);
            const fresh = isFreshlyActivated(t);
            const sleepSentence = sleeping ? activationSentence(t, taskById, collabById) : "";
            return (
            <div key={t.id} className="border-b border-line last:border-b-0">
              <div className="flex items-stretch">
                <div className="pl-3 pt-3">
                  <button
                    data-testid={`task-toggle-${t.id}`}
                    onClick={() => toggle(t)}
                    className="w-5 h-5 shrink-0 mt-0.5 grid place-items-center"
                    aria-label="Toggle"
                    disabled={sleeping}
                    title={sleeping ? "Task još nije spreman za rad" : undefined}
                  >
                    {t.status === "done" ? (
                      <CheckCircle2 className="w-5 h-5 text-forest" />
                    ) : (
                      <Circle className={`w-5 h-5 ${sleeping ? "text-ink-muted/40" : "text-ink-muted hover:text-forest"}`} />
                    )}
                  </button>
                </div>
                <button
                  data-testid={`task-open-${t.id}`}
                  onClick={() => setEditing(t)}
                  className={`flex-1 text-left px-3 py-3 hover:bg-surface-secondary transition-colors ${t.status === "done" ? "opacity-60" : ""} ${sleeping ? "bg-surface-secondary/40" : ""}`}
                >
                  <div className={`text-sm font-medium ${t.status === "done" ? "line-through text-ink-muted" : sleeping ? "text-ink/80" : "text-ink"}`}>
                    {t.title}
                    {fresh && <span className="ml-2 align-middle"><FreshlyActivatedIndicator testid={`task-fresh-${t.id}`} /></span>}
                  </div>
                  <div className="flex flex-wrap items-center gap-2 mt-1 text-xs">
                    <PriorityChip priority={t.priority} />
                    {t.due_date && (
                      <span className={t.status === "open" && !sleeping && t.due_date < today ? "text-terracotta font-semibold" : "text-ink-muted"}>
                        <Clock className="w-3 h-3 inline mr-1" />{t.due_date}
                      </span>
                    )}
                    {t.from_collaborator_id ? (
                      <span className="text-ink-muted inline-flex items-center gap-1">
                        <Link
                          to={`/saradnici/${t.from_collaborator_id}`}
                          className="text-forest hover:underline"
                          onClick={(e) => e.stopPropagation()}
                          data-testid={`task-from-${t.id}`}
                        >
                          {collabById[t.from_collaborator_id]?.name || "—"}
                        </Link>
                        <span>→</span>
                        <Link
                          to={`/saradnici/${t.collaborator_id}`}
                          className="text-forest hover:underline"
                          onClick={(e) => e.stopPropagation()}
                          data-testid={`task-to-${t.id}`}
                        >
                          {collabById[t.collaborator_id]?.name || "—"}
                        </Link>
                      </span>
                    ) : (
                      <>
                        <span className="text-ink-muted">
                          {t.direction === "to_me" ? "on duguje meni" : "ja dugujem"}
                        </span>
                        {collabById[t.collaborator_id] && (
                          <Link to={`/saradnici/${t.collaborator_id}`} className="text-forest hover:underline" onClick={(e) => e.stopPropagation()}>
                            {collabById[t.collaborator_id].name}
                          </Link>
                        )}
                      </>
                    )}
                    {t.ai_created && <span className="text-xs text-forest">✦ AI</span>}
                  </div>
                  {sleeping && (
                    <div className="mt-2" data-testid={`task-sleeping-${t.id}`}>
                      <SleepingIndicator sentence={sleepSentence || t.unlock_note || "Aktiviraće se kada se završi prethodni korak."} />
                    </div>
                  )}
                </button>
              </div>
            </div>
          );})
        )}
      </div>

      {editing && (
        <TaskEditDialog
          open={!!editing}
          onOpenChange={(v) => !v && setEditing(null)}
          task={editing}
          collaborators={collabs}
          onChanged={() => { load(); }}
          onDeleted={() => { setEditing(null); load(); }}
        />
      )}
      <TaskDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        collaborators={collabs}
        projects={projects}
        onCreated={load}
      />
    </div>
  );
}

function PriorityChip({ priority }) {
  const m = { visok: ["#C65D47", "#FDF0ED", "Visok"], srednji: ["#D99A5B", "#FDF6F0", "Srednji"], nizak: ["#82937E", "#F3F5F2", "Nizak"] }[priority] || ["#5C635B", "#F0EFEA", priority];
  return <span className="badge-priority" style={{ background: m[1], color: m[0], borderColor: m[0] }}>{m[2]}</span>;
}
